import type { JSX } from 'react';
import type { WireSourceInstance } from '@logistics/contracts';
import { cn } from '@/lib/utils';
import { fieldLabel, segment } from '../shell/look';
import { useInstances, useKinds } from '../sources/use-sources';
import { groupByKind, toggleKind } from './source-access';
import type { SourceMode } from './source-access';

interface Props {
  readonly mode: SourceMode;
  readonly sourceIds: readonly string[];
  readonly onMode: (mode: SourceMode) => void;
  readonly onSourceIds: (ids: string[]) => void;
}

/**
 * Which sources a person may read: every one (including ones connected later),
 * or exactly the accounts ticked — two Samsara organisations of three, say.
 */
export function SourceAccessPicker({ mode, sourceIds, onMode, onSourceIds }: Props): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <div className={fieldLabel}>
        Sources
        <div className={cn(segment.group, 'bg-surf2 w-fit')} role="radiogroup" aria-label="Sources">
          {(['all', 'only'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => onMode(m)}
              className={mode === m ? segment.on : segment.off}
            >
              {m === 'all' ? 'All sources' : 'Only selected'}
            </button>
          ))}
        </div>
      </div>
      {mode === 'only' && <InstanceList sourceIds={sourceIds} onSourceIds={onSourceIds} />}
    </div>
  );
}

function InstanceList({
  sourceIds,
  onSourceIds,
}: Pick<Props, 'sourceIds' | 'onSourceIds'>): JSX.Element {
  const instances = useInstances();
  const kinds = useKinds();
  const labelOf = (kind: string): string => kinds.data?.find((k) => k.kind === kind)?.label ?? kind;
  const groups = groupByKind(instances.data ?? []);
  return (
    <div
      role="group"
      aria-label="Allowed sources"
      className="border-line bg-surf2 flex max-h-64 flex-col overflow-y-auto rounded-[9px] border"
    >
      {instances.isPending && <div className="text-mute p-3 text-[13px]">Loading sources…</div>}
      {instances.data?.length === 0 && (
        <div className="text-mute p-3 text-[13px]">No sources are connected yet.</div>
      )}
      {groups.map(({ kind, items }) => (
        <KindGroup
          key={kind}
          label={labelOf(kind)}
          items={items}
          sourceIds={sourceIds}
          onSourceIds={onSourceIds}
        />
      ))}
    </div>
  );
}

function KindGroup({
  label,
  items,
  sourceIds,
  onSourceIds,
}: {
  label: string;
  items: readonly WireSourceInstance[];
} & Pick<Props, 'sourceIds' | 'onSourceIds'>): JSX.Element {
  const ticked = items.filter((i) => sourceIds.includes(i.sourceId)).length;
  const toggle = (id: string): void =>
    onSourceIds(sourceIds.includes(id) ? sourceIds.filter((s) => s !== id) : [...sourceIds, id]);
  return (
    <div className="border-line border-b last:border-b-0">
      <label className="flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium">
        <input
          type="checkbox"
          aria-label={`All ${label} accounts`}
          checked={ticked === items.length}
          ref={(box) => {
            if (box !== null) box.indeterminate = ticked > 0 && ticked < items.length;
          }}
          onChange={() => onSourceIds(toggleKind(sourceIds, items))}
        />
        <span className="flex-1">{label}</span>
        <span className="text-mute font-mono text-[11px]">
          {ticked}/{items.length}
        </span>
      </label>
      {items.map((item) => (
        <label
          key={item.sourceId}
          className={cn(
            'flex items-center gap-2.5 py-1.5 pr-3 pl-8 text-[13px]',
            !item.enabled && 'text-mute',
          )}
        >
          <input
            type="checkbox"
            checked={sourceIds.includes(item.sourceId)}
            onChange={() => toggle(item.sourceId)}
          />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {!item.enabled && <span className="font-mono text-[11px]">paused</span>}
        </label>
      ))}
    </div>
  );
}
